import React, { useState, useEffect } from 'react';
import { FiPlus, FiTrash2, FiSave, FiInfo, FiClock, FiDollarSign, FiCheck, FiAlertCircle } from 'react-icons/fi';

const CancellationPolicy = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [policy, setPolicy] = useState({
    name: 'Cancellation Policy',
    description: 'Cancellation charges applicable based on time before appointment',
    fullRefundBeforeHours: 4,
    chargeSlabs: [
      {
        fromHours: 0,
        toHours: 1,
        chargePercentage: 100,
        description: 'Cancellation within 1 hour - No refund'
      },
      {
        fromHours: 1,
        toHours: 2,
        chargePercentage: 50,
        description: 'Cancellation between 1-2 hours - 50% charge'
      },
      {
        fromHours: 2,
        toHours: 4,
        chargePercentage: 25,
        description: 'Cancellation between 2-4 hours - 25% charge'
      }
    ],
    refundConvenienceFees: false,
    refundProcessingDays: 5
  });

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.bookwie.com';

  useEffect(() => {
    fetchPolicy();
  }, []);

  const fetchPolicy = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/admin/cancellation-policy`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success && data.data) {
        setPolicy(data.data);
      }
    } catch (error) {
      console.error('Error fetching policy:', error);
      alert('Failed to load cancellation policy');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSlab = () => {
    const lastSlab = policy.chargeSlabs[policy.chargeSlabs.length - 1];
    const newSlab = {
      fromHours: lastSlab ? lastSlab.toHours : 0,
      toHours: lastSlab ? lastSlab.toHours + 1 : 1,
      chargePercentage: 0,
      description: ''
    };
    setPolicy({
      ...policy,
      chargeSlabs: [...policy.chargeSlabs, newSlab]
    });
  };

  const handleRemoveSlab = (index) => {
    const newSlabs = policy.chargeSlabs.filter((_, i) => i !== index);
    setPolicy({
      ...policy,
      chargeSlabs: newSlabs
    });
  };

  const handleSlabChange = (index, field, value) => {
    const newSlabs = [...policy.chargeSlabs];
    newSlabs[index][field] = field === 'description' ? value : parseFloat(value) || 0;
    setPolicy({
      ...policy,
      chargeSlabs: newSlabs
    });
  };

  const validatePolicy = () => {
    // Validate full refund hours
    if (policy.fullRefundBeforeHours <= 0) {
      alert('Full refund hours must be greater than 0');
      return false;
    }

    // Validate slabs
    if (policy.chargeSlabs.length === 0) {
      alert('At least one charge slab is required');
      return false;
    }

    for (let i = 0; i < policy.chargeSlabs.length; i++) {
      const slab = policy.chargeSlabs[i];
      
      if (slab.fromHours < 0 || slab.toHours <= 0) {
        alert(`Slab ${i + 1}: Invalid hour range`);
        return false;
      }

      if (slab.fromHours >= slab.toHours) {
        alert(`Slab ${i + 1}: From hours must be less than To hours`);
        return false;
      }

      if (slab.chargePercentage < 0 || slab.chargePercentage > 100) {
        alert(`Slab ${i + 1}: Charge percentage must be between 0 and 100`);
        return false;
      }

      // Check for overlapping slabs
      for (let j = i + 1; j < policy.chargeSlabs.length; j++) {
        const otherSlab = policy.chargeSlabs[j];
        if (
          (slab.fromHours < otherSlab.toHours && slab.toHours > otherSlab.fromHours)
        ) {
          alert(`Slabs ${i + 1} and ${j + 1} overlap. Please fix the hour ranges.`);
          return false;
        }
      }
    }

    return true;
  };

  const handleSave = async () => {
    if (!validatePolicy()) {
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/admin/cancellation-policy`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(policy)
      });

      const data = await response.json();
      if (data.success) {
        alert('Cancellation policy updated successfully');
        fetchPolicy();
      } else {
        alert(data.message || 'Failed to update policy');
      }
    } catch (error) {
      console.error('Error saving policy:', error);
      alert('Failed to save cancellation policy');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="py-32 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading cancellation policy...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-gray-900">Cancellation Policy</h1>
            <p className="text-gray-600 mt-1">Configure cancellation charges and refund rules</p>
          </div>
          
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiSave className="mr-2" />
            {saving ? 'Saving...' : 'Save Policy'}
          </button>
        </div>
      </div>

      {/* Info Alert */}
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded-r-lg">
        <div className="flex">
          <FiInfo className="text-blue-500 mt-0.5 mr-3 flex-shrink-0" size={20} />
          <div>
            <h3 className="text-blue-900 font-semibold mb-1">How Cancellation Policy Works</h3>
            <ul className="text-blue-800 text-sm space-y-1">
              <li>• If user cancels <strong>before {policy.fullRefundBeforeHours} hours</strong>, they get full <strong>subtotal refund</strong></li>
              <li>• Convenience fees are {policy.refundConvenienceFees ? 'refunded' : 'NOT refunded'}</li>
              <li>• Within {policy.fullRefundBeforeHours} hours, charge slabs apply based on time remaining</li>
              <li>• Charge percentage is deducted from subtotal, remaining amount is refunded</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Settings */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Settings */}
          <div className="bg-white rounded-3xl shadow-card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Settings</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Policy Name
                </label>
                <input
                  type="text"
                  value={policy.name}
                  onChange={(e) => setPolicy({ ...policy, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Standard Cancellation Policy"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={policy.description}
                  onChange={(e) => setPolicy({ ...policy, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Brief description of the policy"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <FiClock className="mr-2" />
                    Full Refund Before (Hours)
                  </label>
                  <input
                    type="number"
                    value={policy.fullRefundBeforeHours}
                    onChange={(e) => setPolicy({ ...policy, fullRefundBeforeHours: parseFloat(e.target.value) || 0 })}
                    min="0"
                    step="0.5"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Cancel before this time = full subtotal refund</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Refund Processing Days
                  </label>
                  <input
                    type="number"
                    value={policy.refundProcessingDays}
                    onChange={(e) => setPolicy({ ...policy, refundProcessingDays: parseInt(e.target.value) || 5 })}
                    min="1"
                    max="30"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Business days for refund processing</p>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="refundConvenienceFees"
                  checked={policy.refundConvenienceFees}
                  onChange={(e) => setPolicy({ ...policy, refundConvenienceFees: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="refundConvenienceFees" className="ml-2 text-sm text-gray-700">
                  Refund convenience fees on cancellation
                </label>
              </div>
            </div>
          </div>

          {/* Charge Slabs */}
          <div className="bg-white rounded-3xl shadow-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Cancellation Charge Slabs</h2>
                <p className="text-sm text-gray-600">Define charges for cancellations within {policy.fullRefundBeforeHours} hours</p>
              </div>
              <button
                onClick={handleAddSlab}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <FiPlus className="mr-2" />
                Add Slab
              </button>
            </div>

            <div className="space-y-4">
              {policy.chargeSlabs.map((slab, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-gray-900">Slab {index + 1}</h3>
                    {policy.chargeSlabs.length > 1 && (
                      <button
                        onClick={() => handleRemoveSlab(index)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <FiTrash2 />
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        From Hours
                      </label>
                      <input
                        type="number"
                        value={slab.fromHours}
                        onChange={(e) => handleSlabChange(index, 'fromHours', e.target.value)}
                        min="0"
                        step="0.5"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        To Hours
                      </label>
                      <input
                        type="number"
                        value={slab.toHours}
                        onChange={(e) => handleSlabChange(index, 'toHours', e.target.value)}
                        min="0"
                        step="0.5"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Charge %
                      </label>
                      <input
                        type="number"
                        value={slab.chargePercentage}
                        onChange={(e) => handleSlabChange(index, 'chargePercentage', e.target.value)}
                        min="0"
                        max="100"
                        step="5"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <input
                      type="text"
                      value={slab.description}
                      onChange={(e) => handleSlabChange(index, 'description', e.target.value)}
                      placeholder="e.g., Cancellation within 1 hour - No refund"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>

                  <div className="mt-3 bg-blue-50 border border-blue-200 rounded p-2">
                    <p className="text-xs text-blue-800">
                      <strong>Range:</strong> {slab.fromHours}h - {slab.toHours}h before booking | 
                      <strong> Charge:</strong> {slab.chargePercentage}% | 
                      <strong> Refund:</strong> {100 - slab.chargePercentage}%
                    </p>
                  </div>
                </div>
              ))}

              {policy.chargeSlabs.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <FiAlertCircle className="mx-auto mb-2" size={32} />
                  <p>No charge slabs defined. Click "Add Slab" to create one.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Preview/Summary */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-3xl shadow-card p-6 sticky top-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Policy Summary</h2>
            
            <div className="space-y-4">
              <div className="bg-green-50 border-l-4 border-green-500 p-3 rounded-r">
                <div className="flex items-start">
                  <FiCheck className="text-green-600 mt-0.5 mr-2 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-green-900">Full Refund Zone</p>
                    <p className="text-xs text-green-700 mt-1">
                      Cancel ≥ {policy.fullRefundBeforeHours}h before = Full subtotal refund
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">Charge Slabs:</p>
                {policy.chargeSlabs
                  .sort((a, b) => a.fromHours - b.fromHours)
                  .map((slab, index) => (
                  <div key={index} className="bg-orange-50 border-l-4 border-orange-500 p-3 rounded-r">
                    <p className="text-xs font-medium text-orange-900">
                      {slab.fromHours}h - {slab.toHours}h before
                    </p>
                    <p className="text-xs text-orange-700 mt-1">
                      Charge: {slab.chargePercentage}% | Refund: {100 - slab.chargePercentage}%
                    </p>
                  </div>
                ))}
              </div>

              <div className="pt-4 border-t border-gray-200">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Convenience Fees:</span>
                    <span className={`font-medium ${policy.refundConvenienceFees ? 'text-green-600' : 'text-red-600'}`}>
                      {policy.refundConvenienceFees ? 'Refunded' : 'Not Refunded'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Processing Time:</span>
                    <span className="font-medium text-gray-900">{policy.refundProcessingDays} days</span>
                  </div>
                </div>
              </div>

              {/* Example Calculation */}
              <div className="pt-4 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Example:</h3>
                <div className="bg-gray-50 p-3 rounded text-xs space-y-1">
                  <p className="text-gray-700">Booking Amount: ₹1000</p>
                  <p className="text-gray-700">Convenience Fee: ₹25</p>
                  <p className="text-gray-700">Total Paid: ₹1025</p>
                  <div className="pt-2 mt-2 border-t border-gray-300">
                    <p className="font-medium text-gray-900">If cancelled 5h before:</p>
                    <p className="text-green-600">Refund: ₹{policy.refundConvenienceFees ? '1025' : '1000'} (Full)</p>
                  </div>
                  {policy.chargeSlabs.length > 0 && (
                    <div className="pt-2 mt-2 border-t border-gray-300">
                      <p className="font-medium text-gray-900">If cancelled 1.5h before:</p>
                      {(() => {
                        const slab = policy.chargeSlabs.find(s => 1.5 >= s.fromHours && 1.5 < s.toHours);
                        const charge = slab ? (1000 * slab.chargePercentage) / 100 : 1000;
                        const refund = slab ? 1000 - charge : 0;
                        return (
                          <p className="text-orange-600">
                            Refund: ₹{refund.toFixed(0)} ({slab ? slab.chargePercentage : 100}% charge)
                          </p>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CancellationPolicy;

